import type { GameCard } from "../shared/deck";
import type { CardCollection } from "../shared/collections";
import { getSymbol, type GameSymbol } from "../shared/symbols";
import { layoutSymbols } from "../shared/layout";
import { symbolName, t } from "./i18n";

/** Keep a collection's own labels: IDs deliberately overlap the built-in deck. */
export function resolveCardSymbol(
  id: number,
  collection?: CardCollection | null,
): GameSymbol {
  if (collection) {
    const custom = collection.symbols.find((symbol) => symbol.id === id);
    if (!custom) throw new Error(t("Le symbole {number} manque dans ce jeu.", { number: id + 1 }));
    return { id: custom.id, name: custom.name, assetPath: custom.image, emoji: "", color: "#102233" };
  }
  const symbol = getSymbol(id);
  return { ...symbol, name: symbolName(id) };
}

export function createCard(
  card: GameCard,
  options: {
    interactive?: boolean;
    onSymbol?: (id: number) => void;
    label?: string;
    collection?: CardCollection | null;
  } = {},
): HTMLElement {
  const root = document.createElement("div");
  root.className = "game-card";
  root.setAttribute(
    "aria-label",
    options.label ?? t("Carte {number}", { number: card.id + 1 }),
  );
  root.setAttribute("role", "group");
  for (const placement of layoutSymbols(card)) {
    const symbol = resolveCardSymbol(placement.symbolId, options.collection);
    const name = symbol.name;
    const el = document.createElement(options.interactive ? "button" : "span");
    el.className = "card-symbol";
    el.dataset.symbolId = String(symbol.id);
    el.style.left = `${placement.x * 100}%`;
    el.style.top = `${placement.y * 100}%`;
    el.style.width = `${placement.size * 100}%`;
    el.style.height = `${placement.size * 100}%`;
    el.style.fontSize = `${placement.size * 88}cqw`;
    el.style.transform = `translate(-50%, -50%) rotate(${placement.rotation}deg)`;
    el.setAttribute("aria-label", name);
    el.title = name;
    if (symbol.assetPath) {
      const img = document.createElement("img");
      img.alt = name;
      img.draggable = false;
      const localPng = /^\/symbols\/([^/]+)\.png$/.exec(symbol.assetPath);
      if (localPng) {
        const picture = document.createElement("picture");
        picture.style.width = "100%";
        picture.style.height = "100%";
        const screen = document.createElement("source");
        screen.media = "screen";
        screen.type = "image/webp";
        screen.srcset = `/symbols/web/${localPng[1]}.webp`;
        // Keep the original PNG for print and for any missing web derivative.
        img.addEventListener("error", () => screen.remove(), { once: true });
        picture.append(screen, img);
        el.append(picture);
      } else {
        el.append(img);
      }
      img.src = symbol.assetPath;
    } else {
      el.textContent = symbol.emoji;
    }
    if (options.interactive) {
      (el as HTMLButtonElement).type = "button";
      el.addEventListener("click", () => options.onSymbol?.(symbol.id));
    }
    root.append(el);
  }
  return root;
}
